// Sucks having to create these two functions,
// instead of using the same thing Vue uses.

import { Comment, Text, Fragment } from "vue"

/**
 * Returns true when the slot has renderable content
 * @param {Function | undefined} slotFn
 * @returns {Boolean}
 */
export function slotHasContent(slotFn) {
    if (!slotFn) return false

    const nodes = slotFn()

    const hasRenderableContent = vnodes => {
        return vnodes.some(vnode => {
            if (vnode.type === Comment) return false
            if (vnode.type === Text && !vnode.children?.toString().trim())
                return false
            if (vnode.type === Fragment && Array.isArray(vnode.children)) {
                return hasRenderableContent(vnode.children)
            }
            return true
        })
    }

    return hasRenderableContent(nodes)
}

/**
 * Returns true if klass is in a format Vue understands.
 */
export function isValidVueClass(klass) {
    return (
        Array.isArray(klass) ||
        typeof klass == "object" ||
        typeof klass == "string"
    )
}

/**
 * Accepts a "class list" in any of the formats Vue understands (String, Array, Object)
 * and returns an array of classes.
 */
export function normalizeVueClass(klass) {
    if (!isValidVueClass(klass)) {
        throw new Error(`Invalid vue class: ${klass}`)
    }

    if (Array.isArray(klass)) {
        return klass
    } else if (typeof klass == "string") {
        return klass.split(/\s+/)
    } else if (typeof klass == "object") {
        const classes = []
        for (let key in klass) {
            if (Object.hasOwn(klass, key) && klass[key]) {
                classes.push(key)
            }
        }
        return classes
    } else {
        throw new Error("Shouldn't be here")
    }
}

/**
 * Climb the component hierarchy until a reference is found.
 */
function findReference(name, cmp) {
    let p = cmp
    while (!(name in p.$refs) && p.$parent) p = p.$parent
    const ref = p.$refs[name]
    if (!ref) return null

    return ref
}

/**
 * Return the actual DOM reference
 */
export function findDomReference(name, cmp) {
    let ref = findReference(name, cmp)
    if (!ref || ref.length === 0) return null
    if (ref.length) {
        ref = ref[0]
    }
    return ref.$el ? ref.$el : ref
}

/**
 * Given an array of vue-router navigation guards,
 * returns a guard that works as a combination of them all:
 * - If a guard calls next() without arguments, the next guard of the array is called.
 * - Otherwise, vue-router's next() is called and no more guards are executed.
 */
export function combineNavigationGuards(guards) {
    if (guards.length === 0) throw new Error("No guards received")
    if (guards.length === 1) return guards[0]

    const combineTwo = (guard1, guard2) => (to, from, finalNext) => {
        const next = (...args) => {
            if (args.length === 0) {
                guard2(to, from, finalNext)
            } else {
                finalNext(...args)
            }
        }
        guard1(to, from, next)
    }

    return guards.reduce(combineTwo)
}

/**
 * See: guides/functional/logout.md
 * url: string?
 * message: string?
 * type: redirection | okn_logout_screen | undefined
 */
export async function logoutRedirection({
    router,
    query,
    url,
    message,
    type,
    assign = window.location.assign.bind(window.location),
}) {
    switch (type) {
        case "redirection":
            assign(url)
            break

        case "okn_logout_screen":
            await router.push({
                path: "/logout",
                query: {
                    url: url ? encodeURI(url) : null,
                    message: message ? encodeURI(message) : null,
                },
            })
            break

        default:
            await router.push({ path: "/login", query })
            break
    }
}
